import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import styles from './LegalPages.module.css';

function TermsConditions() {
  return (
    <>
      <SEO
        title="Terms & Conditions"
        description="Terms and Conditions for Sapienta VC website and services."
        canonical="https://sapienta.vc/terms-conditions"
      />
      <div className={styles.container}>
      <p className={styles.effectiveDate}>Effective Date: 07/11/2025</p>
      
      <div className={styles.section}>
        <h2 className={styles.subHeading}>1. Introduction</h2>
        <p className={styles.text}>
          Welcome to Sapienta. These Terms and Conditions ("Terms", "Agreement") govern your access to and use of the website and services provided by Sapienta, a holding company, and its associated ventures ("Subsidiaries"). By accessing or using our website and services, you agree to abide by and be bound by these Terms. If you do not agree to these Terms, please refrain from using the website and services.
        </p>
      </div>

      <div className={styles.section}>
        <h2 className={styles.subHeading}>2. Definitions</h2>
        <p className={styles.text}>
          "Sapienta," "we," "us," or "our" refers to Sapienta Holding Company and its subsidiaries or ventures.
        </p>
        <p className={styles.text}>
          "Website" refers to www.sapienta.vc, or any other domain or subdomain operated by Sapienta or its subsidiaries.
        </p>
        <p className={styles.text}>
          "User," "you," or "your" refers to any individual, business, or entity using the website or services provided by Sapienta or its subsidiaries.
        </p>
        <p className={styles.text}>
          "Services" refers to any products, services, or offerings provided by Sapienta or its ventures.
        </p>
      </div>

      <div className={styles.section}>
        <h2 className={styles.subHeading}>3. Scope of Services</h2>
        <p className={styles.text}>
          Sapienta is a holding company with a portfolio of subsidiaries engaged in various business activities. This website serves as the main platform for connecting users to the services and ventures of Sapienta and its subsidiaries. The services available may vary depending on the subsidiary you are interacting with, and these Terms apply to the overall Sapienta group, including but not limited.
        </p>
        <p className={styles.text}>
          Each subsidiary may also have specific Terms and Conditions that apply to its unique services, and these should be reviewed separately.
        </p>
      </div>

      <div className={styles.section}>
        <h2 className={styles.subHeading}>4. User Eligibility</h2>
        <p className={styles.text}>To use the website or services, you must:</p>
        <ul className={styles.list}>
          <li className={styles.listItem}>Be at least 18 years old, or the legal age of majority in your jurisdiction.</li>
          <li className={styles.listItem}>Have the legal capacity to enter into a binding contract.</li>
          <li className={styles.listItem}>Not be prohibited from using the website or services under applicable law or due to previous violations of terms or policies.</li>
        </ul>
      </div>

      <div className={styles.section}>
        <h2 className={styles.subHeading}>5. User Account Creation and Security</h2>
        <p className={styles.text}>If you register for an account to access services, you agree to:</p>
        <ul className={styles.list}>
          <li className={styles.listItem}>Provide accurate, current, and complete information.</li>
          <li className={styles.listItem}>Keep your account information secure and confidential.</li>
          <li className={styles.listItem}>Immediately notify Sapienta of any unauthorized access to your account.</li>
          <li className={styles.listItem}>Be responsible for all activities under your account.</li>
        </ul>
        <p className={styles.text}>
          Sapienta reserves the right to suspend or terminate your account for violations of these Terms or for any other reason deemed necessary.
        </p>
      </div>

      <div className={styles.section}>
        <h2 className={styles.subHeading}>6. Use of the Website and Services</h2>
        <p className={styles.text}>
          Sapienta grants you a limited, non-exclusive, non-transferable license to access and use the website and services. You agree to:
        </p>
        <ul className={styles.list}>
          <li className={styles.listItem}>Use the website only for lawful purposes.</li>
          <li className={styles.listItem}>Comply with all applicable laws and regulations, including data protection and privacy laws.</li>
          <li className={styles.listItem}>Not engage in activities that could disrupt the operation of the website or harm Sapienta's systems or reputation.</li>
        </ul>
        <p className={styles.text}>You may not:</p>
        <ul className={styles.list}>
          <li className={styles.listItem}>Reverse engineer, decompile, or disassemble any part of the website.</li>
          <li className={styles.listItem}>Use the website for unlawful activities or unauthorized commercial purposes.</li>
          <li className={styles.listItem}>Submit or upload any content that is harmful, defamatory, or infringing upon intellectual property rights.</li>
        </ul>
      </div>

      <div className={styles.section}>
        <h2 className={styles.subHeading}>7. Pricing and Payment Terms (if applicable)</h2>
        <p className={styles.text}>For any paid services, users agree to:</p>
        <ul className={styles.list}>
          <li className={styles.listItem}>Pay the applicable fees for products or services as set forth by Sapienta or its subsidiaries.</li>
          <li className={styles.listItem}>Provide valid payment information and keep it up to date.</li>
          <li className={styles.listItem}>Understand that any payments are non-refundable unless specified otherwise.</li>
        </ul>
        <p className={styles.text}>
          You may be subject to additional terms or conditions that apply to the specific subsidiary providing the services you purchase.
        </p>
      </div>

      <div className={styles.section}>
        <h2 className={styles.subHeading}>8. Privacy and Data Protection</h2>
        <p className={styles.text}>
          Your use of the website and services is also governed by our <Link to="/privacy-policy" className={styles.link}>Privacy Policy</Link>, which outlines how we collect, use, store, and protect your personal data. By using the website or services, you consent to the collection and processing of your personal data as described in the Privacy Policy.
        </p>
      </div>

      <div className={styles.section}>
        <h2 className={styles.subHeading}>9. Content Ownership and Intellectual Property</h2>
        <p className={styles.text}>
          <strong>Ownership:</strong> All content, materials, and intellectual property on the website, including logos, trademarks, graphics, text, and software, are owned by Sapienta or its licensors.
        </p>
        <p className={styles.text}>
          <strong>License:</strong> You are granted a limited, revocable license to use the content solely for personal, non-commercial purposes.
        </p>
        <p className={styles.text}>
          <strong>User-Generated Content:</strong> If you submit content (e.g., reviews, feedback, forum posts), you grant Sapienta and its subsidiaries a worldwide, royalty-free, transferable license to use, display, and distribute that content.
        </p>
      </div>

      <div className={styles.section}>
        <h2 className={styles.subHeading}>10. Third-Party Links and Services</h2>
        <p className={styles.text}>
          The website may contain links to third-party websites or services that are not owned or controlled by Sapienta. We are not responsible for the content, privacy practices, or services provided by these third-party websites. We encourage you to review their terms and privacy policies before engaging with them.
        </p>
      </div>

      <div className={styles.section}>
        <h2 className={styles.subHeading}>11. Disclaimers</h2>
        <p className={styles.text}>
          <strong>No Warranty:</strong> The website and services are provided "as is" and without any warranties, express or implied. Sapienta does not guarantee that the website will be error-free or that the services will meet your specific requirements.
        </p>
        <p className={styles.text}>
          <strong>Limitation of Liability:</strong> To the fullest extent permitted by law, Sapienta, its subsidiaries, and affiliates are not liable for any indirect, incidental, or consequential damages arising from your use of the website or services.
        </p>
      </div>

      <div className={styles.section}>
        <h2 className={styles.subHeading}>12. Indemnification</h2>
        <p className={styles.text}>
          You agree to indemnify, defend, and hold harmless Sapienta, its subsidiaries, officers, employees, agents, and affiliates from any claims, damages, liabilities, and expenses (including legal fees) arising from your use of the website or services, violation of these Terms, or infringement of third-party rights.
        </p>
      </div>

      <div className={styles.section}>
        <h2 className={styles.subHeading}>13. Governing Law and Dispute Resolution</h2>
        <p className={styles.text}>
          These Terms are governed by the laws of [Insert Jurisdiction], without regard to conflict of law principles. Any disputes arising out of or in connection with these Terms shall be resolved through binding arbitration in [Insert Location].
        </p>
      </div>

      <div className={styles.section}>
        <h2 className={styles.subHeading}>14. Modifications to Terms</h2>
        <p className={styles.text}>
          Sapienta reserves the right to modify or update these Terms at any time. Any changes will be posted on this page, and the effective date will be updated accordingly. You are responsible for reviewing these Terms periodically. Continued use of the website or services after changes have been posted constitutes acceptance of the modified Terms.
        </p>
      </div>

      <div className={styles.section}>
        <h2 className={styles.subHeading}>15. Termination</h2>
        <p className={styles.text}>
          Sapienta may suspend or terminate your access to the website or services at any time, with or without notice, if you violate these Terms or engage in harmful activities. Upon termination, you must immediately cease all use of the website and services.
        </p>
      </div>

      <div className={styles.section}>
        <h2 className={styles.subHeading}>16. Severability</h2>
        <p className={styles.text}>
          If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions shall remain in full force and effect.
        </p>
      </div>

      <div className={styles.section}>
        <h2 className={styles.subHeading}>17. Contact Information</h2>
        <p className={styles.text}>
          If you have any questions or concerns about these Terms and Conditions, please contact us:
        </p>
        <p className={styles.text}>
          Sapienta Group of Ventures<br />
          Email: legal@sapienta.vc<br />
          Address: 3811 Ditmars Blvd<br />
          Astoria, NY 11105-1803, US
        </p>
      </div>
    </div>
    </>
  );
}

export default TermsConditions; 